import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  SectionHeader,
  SelectField,
  TextField,
  TextareaField,
} from "../../components/design-system/index.js";
import { Toast, useToast } from "../../components/toast.js";
import {
  createEstateItem,
  listEstateItems,
  updateEstateItem,
  type BurialPreference,
  type EstateBurialWishesItem,
} from "../../lib/api-modules/estate.js";

/**
 * /estate/burial-wishes — Phase 9.
 *
 * Single form, single record per household (upsert). Helper for the
 * spouse + family — not a legally binding instruction by itself.
 */
export function EstateBurialWishesPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const itemsQ = useQuery({
    queryKey: ["estate-items"],
    queryFn: listEstateItems,
  });
  const existing = itemsQ.data?.items.find(
    (i): i is EstateBurialWishesItem => i.item_type === "burial_wishes",
  );

  const [preference, setPreference] = useState<BurialPreference>("burial");
  const [funeralHome, setFuneralHome] = useState("");
  const [plotLocation, setPlotLocation] = useState("");
  const [religiousConsiderations, setReligiousConsiderations] = useState("");
  const [notes, setNotes] = useState("");

  // Seed from server once.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!seeded && existing) {
      setPreference(existing.burial_preference);
      setFuneralHome(existing.funeral_home ?? "");
      setPlotLocation(existing.plot_location ?? "");
      setReligiousConsiderations(existing.religious_considerations ?? "");
      setNotes(existing.notes ?? "");
      setSeeded(true);
    } else if (!seeded && itemsQ.isFetched && !existing) {
      setSeeded(true);
    }
  }, [seeded, existing, itemsQ.isFetched]);

  const upsertMut = useMutation({
    mutationFn: () => {
      const body = {
        burial_preference: preference,
        funeral_home: funeralHome.trim() || undefined,
        plot_location: plotLocation.trim() || undefined,
        religious_considerations:
          religiousConsiderations.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      if (existing) {
        return updateEstateItem(existing.item_id, body);
      }
      return createEstateItem({ item_type: "burial_wishes", ...body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["estate-items"] });
      toast.show("Saved");
    },
    onError: (err) =>
      toast.show(err instanceof Error ? err.message : "Save failed"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    upsertMut.mutate();
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <Link
        to="/estate"
        className="inline-flex items-center text-sm text-[var(--color-accent)]"
      >
        ← Back to estate
      </Link>
      <SectionHeader
        title="Burial wishes"
        sub="Preferences for your spouse + family. Not legally binding by itself — your will is the authoritative document — but a kindness for whoever has to make these decisions on short notice."
      />
      <Toast message={toast.message} />

      <Card>
        <form onSubmit={onSubmit} className="space-y-3">
          <SelectField
            label="Preference"
            value={preference}
            onChange={(e) => setPreference(e.target.value as BurialPreference)}
          >
            <option value="burial">Burial</option>
            <option value="cremation">Cremation</option>
            <option value="donation">Donation to science</option>
          </SelectField>
          <TextField
            label="Funeral home (optional)"
            value={funeralHome}
            onChange={(e) => setFuneralHome(e.target.value)}
            placeholder="e.g. Frank E. Campbell, NYC"
          />
          <TextField
            label="Plot location (optional)"
            value={plotLocation}
            onChange={(e) => setPlotLocation(e.target.value)}
            placeholder="e.g. Section 14, Lot 23, Greenwood Cemetery"
          />
          <TextareaField
            label="Religious considerations (optional)"
            rows={3}
            value={religiousConsiderations}
            onChange={(e) => setReligiousConsiderations(e.target.value)}
            placeholder="Any religious customs to observe"
          />
          <TextareaField
            label="Notes (optional)"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything else your family should know"
          />
          <div>
            <Button
              type="submit"
              size="sm"
              disabled={upsertMut.isPending}
            >
              {upsertMut.isPending ? "Saving…" : existing ? "Update" : "Save"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
